import mixpanelBrowser from "mixpanel-browser";

import { i18n } from "@/i18n";
import { checkIsAnonymousMixpanelId } from "@/services/mixpanelIdentity";
import { FlagsCustomPropertiesType } from "@/services/mixpanelTypes";
import { envVariables } from "@/utils/envVariables";

const token = envVariables.EXPO_PUBLIC_MIXPANEL_TOKEN;

export const initMixpanel = () => {
  mixpanelBrowser.init(token, {
    autocapture: false,
    track_pageview: false,
    record_sessions_percent: 0,
    record_heatmap_data: true,
    debug: __DEV__,
  });
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const initSessionReplay = (distinctId: string) => {
  mixpanelBrowser.start_session_recording();
};

export const stopSessionReplay = () => {
  mixpanelBrowser.stop_session_recording();
};

const mixpanel = {
  track(eventName: string, properties?: Record<string, unknown>) {
    mixpanelBrowser.track(eventName, properties);
  },

  identify(distinctId: string) {
    mixpanelBrowser.identify(distinctId);
    mixpanelBrowser.people.set_once({ $created: new Date().toISOString() });
    return Promise.resolve();
  },

  resetIfIdentified() {
    const distinctId: unknown = mixpanelBrowser.get_distinct_id();
    const deviceId: unknown = mixpanelBrowser.get_property("$device_id");

    if (
      checkIsAnonymousMixpanelId({
        distinctId: typeof distinctId === "string" ? distinctId : undefined,
        deviceId: typeof deviceId === "string" ? deviceId : undefined,
      })
    ) {
      return Promise.resolve();
    }

    mixpanelBrowser.reset();
    return Promise.resolve();
  },

  flush() {
    // mixpanel-browser auto-flushes, no manual flush needed
  },

  registerSuperProperties(properties: Record<string, unknown>) {
    mixpanelBrowser.register(properties);
  },

  // Flags stay off until the first call here, so launches that never need them
  // (e.g. signed-in users) don't fetch them, and the first fetch already
  // carries the context instead of being repeated with it.
  async updateFlagsContext(customProperties: FlagsCustomPropertiesType) {
    const context = { custom_properties: customProperties };

    if (!mixpanelBrowser.get_config("flags")) {
      mixpanelBrowser.set_config({ flags: { context } });
      await mixpanelBrowser.flags.load_flags();
      return;
    }

    await mixpanelBrowser.flags.update_context(context);
  },

  getVariantValue({
    flagKey,
    fallbackValue,
  }: {
    flagKey: string;
    fallbackValue: unknown;
  }): Promise<unknown> {
    return mixpanelBrowser.flags.get_variant_value(flagKey, fallbackValue);
  },
};

export const trackElementClicked = ({
  route,
  elementTextKey,
  elementText,
  elementType,
}: {
  route: string | null;
  elementTextKey?: string;
  elementText?: string;
  elementType: string;
}) => {
  if (!elementTextKey && !elementText) return;

  const resolvedText = elementTextKey ? i18n.t(elementTextKey) : elementText;

  mixpanel.track("Element Clicked", {
    route,
    element_text_key: elementTextKey,
    element_text: resolvedText,
    element_type: elementType,
  });
};

export default mixpanel;
